import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import {
  signalCatalog,
  type LimitSet,
  type PlaneKey,
  type Signal,
} from '@/domain/hs1'
import { Alert, Button, Card } from '@/components/ui'
import { pickerGroups, SIDE_CHOICES, type SideChoice } from '@/sim/picker'
import { TracePanel } from '@/sim/TracePanel'
import type { TraceSeries } from '@/sim/TraceChart'
import {
  addChannel,
  channelTones,
  channelsOf,
  closeTrace,
  defaultTraces,
  hasChannel,
  mergeTrace,
  moveTrace,
  pruneChannels,
  removeChannel,
  splitTrace,
  toggleVisible,
  tracesFrom,
  TRACE_HEIGHT,
  TRACE_SETS,
  type Trace,
} from '@/sim/traces'
import { useSimulation } from '@/sim/SimulationProvider'
import { Metric, PageHead, num } from '@/sim/parts'

/**
 * Kanäle — jede gerechnete Reihe als Spur, mehrere untereinander.
 *
 * WOFÜR ES DIESE SEITE GIBT UND WOFÜR NICHT. Die Seite Simulation zeigt sechs
 * feste Diagramme je Gelenk; sie beantwortet die Frage „wie sieht das Knie
 * aus?" und beantwortet sie gut. Sie kann nicht beantworten, wie sich das
 * Kniemoment zur Bodenkraft der anderen Seite verhält — dafür müsste man zwei
 * Reihen nebeneinanderlegen, die in keiner festen Sechsergruppe zusammen
 * vorkommen. Genau das ist hier möglich.
 *
 * DIE SPUREN SIND FLACH UND STEHEN DICHT, weil man Spuren vergleicht, indem man
 * sie gleichzeitig sieht. Sechs Kacheln mit Rahmen, Radius und Achsbeschriftung
 * wären dieselbe Auskunft auf der dreifachen Fläche — und man scrollte zwischen
 * zwei Kurven hin und her, statt sie übereinander zu haben.
 *
 * ZIEHEN MACHT ZWEIERLEI. Über den Rand einer anderen Spur gezogen sortiert es
 * um, in ihre MITTE gezogen legt es beide übereinander. Das ist der einzige Weg
 * zum Überlagern und soll es bleiben: ein zweiter wäre eine zweite Stelle, an
 * der man es suchen muss.
 *
 * DAS VERZEICHNIS WIRD NICHT GEPFLEGT, SONDERN ERZEUGT — sowohl die Kurven als
 * auch die Tasten des Wählers. Eine handgeführte Liste veraltet an dem Tag, an
 * dem jemand eine Grösse in den Kern einbaut und die Liste vergisst.
 */

/** Wo in einer Spur das Überlagern beginnt und wo das Umsortieren endet. */
const MERGE_ZONE = [0.3, 0.7] as const

interface Drag {
  id: string
  from: number
  /** Wohin die Spur fällt, wenn nicht überlagert wird. */
  insertAt: number
  mergeInto: string | null
}

export function SignalsPage() {
  const { state, simulation, phase, setPlaying } = useSimulation()
  const catalog = useMemo(() => signalCatalog(simulation), [simulation])

  const byKey = useMemo(
    () => new Map(catalog.signals.map((s) => [s.key, s] as const)),
    [catalog],
  )

  const [traces, setTraces] = useState<Trace[]>(() =>
    defaultTraces((key) => catalog.signals.some((s) => s.key === key)),
  )
  const [side, setSide] = useState<SideChoice>('R')
  const [plane, setPlane] = useState<PlaneKey>('sag')
  const [dropped, setDropped] = useState(0)
  const [drag, setDrag] = useState<Drag | null>(null)

  /*
    WAS PASSIERT, WENN DIE BEWEGUNG WECHSELT. Das Verzeichnis führt nur, was der
    Durchlauf hergibt — Stehen hat kein Zehengrundgelenk in Bewegung. Kanäle, die
    es nicht mehr gibt, fallen aus den Spuren; eine Spur, die dabei leer wird,
    verschwindet. Die übrigen bleiben stehen, samt Reihenfolge.

    Die Zahl der weggefallenen Kanäle wird gemerkt, weil ein stilles Verschwinden
    aussieht wie ein Fehler.
  */
  const live = useRef(traces)
  live.current = traces

  useEffect(() => {
    const gone = channelsOf(live.current).filter((key) => !byKey.has(key)).length
    setDropped(gone)
    if (gone > 0) setTraces(pruneChannels(live.current, (key) => byKey.has(key)))
  }, [byKey])

  const tones = useMemo(() => channelTones(traces), [traces])
  const groups = useMemo(
    () => pickerGroups(catalog.signals, side, plane),
    [catalog, side, plane],
  )
  const chosen = channelsOf(traces)
  const stance = simulation.metrics.stance

  const toggle = (keys: string[]) => {
    if (keys.length === 0) return
    setDropped(0)
    setTraces((list) => {
      const allOn = keys.every((key) => hasChannel(list, key))
      return keys.reduce(
        (out, key) => (allOn ? removeChannel(out, key) : addChannel(out, key)),
        list,
      )
    })
  }

  /* ------------------------------------------------------------------
     Ziehen: umsortieren oder überlagern

     Mit den Zeiger-Ereignissen und nicht mit der Ziehmechanik des Browsers.
     Die kann eine Kachel aufnehmen und fallen lassen — aber nicht sagen, ob
     der Zeiger über der MITTE einer Spur steht, und genau daran hängt hier
     die Unterscheidung zwischen Umsortieren und Überlagern.
     ------------------------------------------------------------------ */

  const host = useRef<HTMLDivElement>(null)

  const onGrab = (event: ReactPointerEvent, from: number) => {
    event.preventDefault()
    setDrag({ id: traces[from].id, from, insertAt: from, mergeInto: null })
  }

  useEffect(() => {
    if (!drag) return
    const rows = () =>
      [...(host.current?.querySelectorAll<HTMLElement>('.trace') ?? [])].map((el, i) => ({
        i,
        el,
      }))

    const onMove = (event: PointerEvent) => {
      let insertAt = traces.length
      let mergeInto: string | null = null
      for (const { i, el } of rows()) {
        if (i === drag.from) continue
        const r = el.getBoundingClientRect()
        if (event.clientY < r.top) {
          insertAt = i
          break
        }
        if (event.clientY <= r.bottom) {
          const rel = (event.clientY - r.top) / r.height
          if (rel > MERGE_ZONE[0] && rel < MERGE_ZONE[1]) mergeInto = traces[i].id
          insertAt = i + (rel > 0.5 ? 1 : 0)
          break
        }
      }
      setDrag((d) => (d ? { ...d, insertAt, mergeInto } : d))
    }

    const onUp = () => {
      setDrag(null)
      setTraces((list) => {
        if (drag.mergeInto) return mergeTrace(list, drag.id, drag.mergeInto)
        const from = list.findIndex((t) => t.id === drag.id)
        /* Der Zielindex zählt in der Liste MIT der gezogenen Spur; nach dem
           Herausnehmen rutscht alles dahinter um eins vor. */
        return moveTrace(list, from, drag.insertAt > from ? drag.insertAt - 1 : drag.insertAt)
      })
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp, { once: true })
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
  }, [drag, traces])

  return (
    <div className="stack">
      <PageHead
        title="Kanäle"
        actions={
          traces.length > 0 && (
            <Button size="sm" variant="ghost" onClick={() => setTraces([])}>
              Alle leeren
            </Button>
          )
        }
      />

      <div className="metrics">
        <Metric
          label="Verfügbare Kanäle"
          value={String(catalog.signals.length)}
          note={`in ${catalog.groups.length} Gruppen`}
        />
        <Metric
          label="Gewählt"
          value={String(chosen.length)}
          note={
            traces.length === 0
              ? 'keine Spur'
              : `${traces.length} ${traces.length === 1 ? 'Spur' : 'Spuren'}`
          }
        />
        <Metric
          label="Stützstellen je Zyklus"
          value={String(catalog.samples)}
          note={`Zyklusdauer ${num(simulation.metrics.cycleTime, 3)} s`}
        />
        <Metric
          label="Betriebsart"
          value={state.generated ? 'gerechnet' : 'aufgezeichnet'}
          note={`Standphase ${num(stance * 100, 0)} %`}
        />
      </div>

      {dropped > 0 && (
        <Alert
          tone="info"
          title={
            dropped === 1
              ? 'Ein Kanal ist weggefallen'
              : `${dropped} Kanäle sind weggefallen`
          }
        >
          Das Verzeichnis führt nur, was der Durchlauf hergibt. Eine Achse ohne Ausschlag gibt es in
          dieser Bewegung nicht; sie steht wieder im Wähler, sobald sie in einer Bewegung
          ausschlägt.
        </Alert>
      )}

      <div className="traces" ref={host}>
        {traces.length === 0 ? (
          <p className="traces__empty">
            Keine Spur. Unten im Wähler einen Kanal einschalten oder einen fertigen Satz nehmen.
          </p>
        ) : (
          traces.map((trace, index) => {
            const series = trace.keys
              .map((key) => byKey.get(key))
              .filter(isSignal)
              .map(
                (signal): TraceSeries => ({
                  key: signal.key,
                  label: signal.label,
                  unit: signal.unit,
                  values: signal.values,
                  tone: tones.get(signal.key) ?? 1,
                  limit: limitOf(signal.key, simulation.limits),
                }),
              )
            return (
              <Fragment key={trace.id}>
                {/* Wohin die gezogene Spur fällt. Beim Überlagern nicht: dort
                    zeigt die Zielspur selbst, dass sie gemeint ist. */}
                {drag && !drag.mergeInto && drag.insertAt === index && (
                  <div className="traces__drop" />
                )}
              <TracePanel
                trace={trace}
                index={index}
                series={series}
                height={TRACE_HEIGHT}
                stance={stance}
                cursor={phase}
                decimals={Object.fromEntries(series.map((s) => [s.key, decimalsFor(s.unit)]))}
                dragging={drag?.id === trace.id}
                mergeTarget={drag?.mergeInto === trace.id}
                onSplit={() => setTraces((list) => splitTrace(list, trace.id))}
                onToggleVisible={() => setTraces((list) => toggleVisible(list, trace.id))}
                onClose={() => setTraces((list) => closeTrace(list, trace.id))}
                onRemoveChannel={(key) =>
                  setTraces((list) =>
                    /* Nur aus DIESER Spur: wer denselben Kanal in zwei Spuren
                       liegen hat, will ihn in der einen wegnehmen und in der
                       anderen behalten. */
                    list
                      .map((t) =>
                        t.id === trace.id ? { ...t, keys: t.keys.filter((k) => k !== key) } : t,
                      )
                      .filter((t) => t.keys.length > 0),
                  )
                }
                onScrub={(at) => {
                  /* Wer in eine Spur greift, will an diese Stelle — und nicht,
                     dass die Wiedergabe ihm den Finger wegzieht. */
                  setPlaying(false)
                  phase.current = at
                }}
                onGrab={onGrab}
                onMove={(from, to) => setTraces((list) => moveTrace(list, from, to))}
              />
              </Fragment>
            )
          })
        )}

        {drag && !drag.mergeInto && drag.insertAt >= traces.length && (
          <div className="traces__drop" />
        )}
      </div>

      <Card
        title="Wähler"
        eyebrow={`${chosen.length} von ${catalog.signals.length} eingeschaltet`}
      >
        <div className="pick__head">
          <Segment
            label="Seite"
            options={SIDE_CHOICES}
            value={side}
            onChange={(v) => setSide(v as SideChoice)}
          />
          <Segment
            label="Ebene"
            options={PLANE_CHOICES}
            value={plane}
            onChange={(v) => setPlane(v as PlaneKey)}
          />
        </div>

        <div className="pick__sets">
          {TRACE_SETS.map((set) => (
            <button
              key={set.id}
              type="button"
              className="pick__set"
              onClick={() => setTraces(tracesFrom(set.traces, (key) => byKey.has(key)))}
            >
              {set.label}
            </button>
          ))}
        </div>

        <div className="pick__grid">
          {groups.map((group) => (
            <div key={group.id} className="pick__group">
              <h4 className="pick__grouphead">{group.label}</h4>
              <div className="pick__row">
                {group.buttons.map((button) => {
                  const on = button.keys.length > 0 && button.keys.every((k) => hasChannel(traces, k))
                  return (
                    <button
                      key={button.id}
                      type="button"
                      className={['qb', on ? 'qb--on' : '', button.dim ? 'qb--dim' : '']
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => toggle(button.keys)}
                      disabled={button.dim}
                      aria-pressed={on}
                      title={
                        button.dim
                          ? 'In dieser Ebene gibt es die Achse nicht'
                          : `${button.keys.length} ${button.keys.length === 1 ? 'Kanal' : 'Kanäle'}`
                      }
                    >
                      {button.label}
                      {button.unit && <small>{button.unit}</small>}
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Lesehinweise">
        <div className="balance__notes">
          <div>
            <span className="eyebrow">Gemischte Einheiten werden normiert</span>
            <p className="page-lead">
              Liegen in einer Spur Kanäle verschiedener Einheiten, bekommt jeder seinen eigenen
              Bereich und die Fusszeile nennt sie einzeln. Vergleichbar ist dann die Form der
              Kurven, nicht ihr Zahlenwert.
            </p>
          </div>
          <div>
            <span className="eyebrow">Zwei Konventionen, eine Periode</span>
            <p className="page-lead">
              Winkelbahnen und Momente führen {catalog.samples + 1} Werte für {catalog.samples}{' '}
              Schritte; der letzte ist gleich dem ersten. Bodenkraft, Lastanteil und Leistung führen{' '}
              {catalog.samples}. Beide gehören zur selben Periode.
            </p>
          </div>
          <div>
            <span className="eyebrow">Ausblenden ist nicht Entfernen</span>
            <p className="page-lead">
              Ausblenden macht die Spur stumm und lässt die Auswahl stehen; Entfernen nimmt beides.
              Teilen nimmt überlagerte Kanäle wieder auseinander.
            </p>
          </div>
          <div>
            <span className="eyebrow">Nichts davon wird hier gerechnet</span>
            <p className="page-lead">
              Jede Reihe ist aus dem Durchlauf gelesen, jede Grenze aus dem Modell. Auch die
              Ableitungen kommen aus derselben Funktion wie die Diagramme der Seite Simulation.
            </p>
          </div>
        </div>
      </Card>
    </div>
  )
}

const PLANE_CHOICES: { key: PlaneKey; label: string }[] = [
  { key: 'sag', label: 'Sagittal' },
  { key: 'fro', label: 'Frontal' },
  { key: 'tra', label: 'Transversal' },
]

const isSignal = (s: Signal | undefined): s is Signal => s !== undefined

/** Grössere Zahlen brauchen keine Nachkommastelle — Watt und Newton nicht. */
const decimalsFor = (unit: string): number => (unit === 'W' || unit === 'N' ? 0 : 1)

/**
 * Die Grenze einer Kurve, wo es eine gibt.
 *
 * SIE KOMMT AUS DEM MODELL UND NICHT AUS DER SEITE. `simulation.limits` ist
 * derselbe Satz Zahlen, an dem die Seite Simulation ihre Überschreitungen misst;
 * eine zweite Tabelle hier wäre eine zweite Wahrheit über dieselbe Achse.
 *
 * Nur Winkel und Moment tragen eine: für eine Leistung, eine Bodenkraft oder
 * eine Beschleunigung steht im Modell keine Grenze, und eine erfundene wäre
 * schlimmer als keine. STILLGELEGTE ACHSEN STEHEN NICHT IN `limits` — sie haben
 * keinen Bereich mehr, und die Kurve wird dann ohne Grenzlinie gezeichnet.
 */
function limitOf(key: string, limits: LimitSet): { lo: number; hi: number } | undefined {
  const [kind, joint, plane] = key.split('|')
  const limit = limits[joint]?.[plane as PlaneKey]
  if (!limit) return undefined
  if (kind === 'A') return { lo: limit.lo, hi: limit.hi }
  if (kind === 'M') return { lo: -limit.moment, hi: limit.moment }
  return undefined
}

function Segment<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string
  options: { key: T; label: string }[]
  value: T
  onChange: (value: T) => void
}) {
  return (
    <div className="pick__seg">
      <span className="eyebrow">{label}</span>
      <div className="pick__toggle" role="group" aria-label={label}>
        {options.map((option) => (
          <button
            key={option.key}
            type="button"
            className={`pick__toggle-button${option.key === value ? ' pick__toggle-button--on' : ''}`}
            onClick={() => onChange(option.key)}
            aria-pressed={option.key === value}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  )
}
